import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Table, Spinner, Alert, Button,
    ProgressBar, Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function ValeurStock() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('global'); // 'global' ou 'details'

    useEffect(() => {
        fetchValeurStock();
    }, []);

    const fetchValeurStock = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stock_etat.php?action=valeur',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Calcul de la valeur du stock...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error || !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error || "Erreur de chargement"}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchValeurStock}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    const valeurMoyenne = (data.global?.valeur_achat || 0) / (data.global?.total_articles || 1);
    const margePotentielle = (data.global?.valeur_vente || 0) - (data.global?.valeur_achat || 0);
    const tauxMarge = ((margePotentielle / (data.global?.valeur_achat || 1)) * 100).toFixed(1);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                                            boxShadow: '0 10px 20px rgba(16, 185, 129, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-pie-chart-2-line text-success" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Valeur du stock
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Évaluation financière • {data.global?.total_produits || 0} produits
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant={viewMode === 'global' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('global')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-pie-chart-2-line me-1"></i>Global
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'details' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('details')}
                                        className="rounded-pill px-3 me-2"
                                    >
                                        <i className="ri-list-check-line me-1"></i>Détails
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/stock/etat/instantane')}>
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de résumé principal */}
                    <Row className="g-4 mb-4 stats-row">
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none'
                            }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <span className="badge bg-white bg-opacity-25 text-white mb-2 rounded-pill px-3 py-2">
                                                <i className="ri-shopping-cart-line me-1"></i>
                                                Prix d'achat
                                            </span>
                                            <h3 className="display-7 fw-bold mb-0 mt-2">{formatCurrency(data.global?.valeur_achat || 0)}</h3>
                                            <p className="text-white-50 mt-2 mb-0">
                                                <i className="ri-information-line me-1"></i>
                                                Coût total d'acquisition
                                            </p>
                                        </div>
                                        <div className="bg-white bg-opacity-25 rounded-circle p-3">
                                            <i className="ri-shopping-cart-line fs-1"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm h-100 stat-card" style={{
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                color: 'white',
                                border: 'none'
                            }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <span className="badge bg-white bg-opacity-25 text-white mb-2 rounded-pill px-3 py-2">
                                                <i className="ri-money-euro-circle-line me-1"></i>
                                                Prix de vente
                                            </span>
                                            <h3 className="display-7 fw-bold mb-0 mt-2">{formatCurrency(data.global?.valeur_vente || 0)}</h3>
                                            <p className="text-white-50 mt-2 mb-0">
                                                <i className="ri-information-line me-1"></i>
                                                Valeur de revente potentielle
                                            </p>
                                        </div>
                                        <div className="bg-white bg-opacity-25 rounded-circle p-3">
                                            <i className="ri-money-euro-circle-line fs-1"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Indicateurs clés */}
                    <Row className="g-4 mb-4">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-stack-line text-primary fs-4"></i>
                                        </div>
                                        <div>
                                            <h6 className="text-muted mb-0">Total produits</h6>
                                            <h3 className="mb-0 fw-bold">{data.global?.total_produits || 0}</h3>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-top">
                                        <div className="d-flex justify-content-between text-muted small">
                                            <span>Références actives</span>
                                            <span className="fw-semibold">{data.global?.total_produits || 0}</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-calculator-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <h6 className="text-muted mb-0">Valeur moyenne</h6>
                                            <h3 className="mb-0 fw-bold">{formatCurrency(valeurMoyenne)}</h3>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-top">
                                        <div className="d-flex justify-content-between text-muted small">
                                            <span>Par article</span>
                                            <span className="fw-semibold">{data.global?.total_articles || 0} unités</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-line-chart-line text-info fs-4"></i>
                                        </div>
                                        <div>
                                            <h6 className="text-muted mb-0">Marge potentielle</h6>
                                            <h3 className="mb-0 fw-bold text-success">{formatCurrency(margePotentielle)}</h3>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-top">
                                        <div className="d-flex justify-content-between text-muted small">
                                            <span>Taux de marge</span>
                                            <span className="fw-semibold text-success">{tauxMarge}%</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Vue par catégorie */}
                    {viewMode === 'global' && (
                        <Card className="border-0 shadow-sm mb-4 table-card">
                            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="ri-folder-chart-line me-2 text-primary"></i>
                                    Répartition par catégorie
                                </h5>
                                <Badge bg="primary" className="px-3 py-2">
                                    {data.par_categorie?.length || 0} catégories
                                </Badge>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Catégorie</th>
                                                <th className="text-center">Produits</th>
                                                <th className="text-center">Articles</th>
                                                <th className="text-end">Valeur achat</th>
                                                <th className="text-end">Valeur vente</th>
                                                <th style={{ width: '200px' }}>Répartition</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.par_categorie?.map((c, index) => {
                                                const pourcentage = (c.valeur_achat / data.global?.valeur_achat) * 100;
                                                return (
                                                    <tr key={index} style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                    }}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" 
                                                                     style={{ width: '32px', height: '32px' }}>
                                                                    <i className="ri-folder-line text-primary"></i>
                                                                </div>
                                                                <span className="fw-semibold">{c.nom_categorie || 'Sans catégorie'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="light" text="dark" className="px-3 py-2">
                                                                {c.nb_produits}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="light" text="dark" className="px-3 py-2">
                                                                {c.total_articles}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end fw-bold text-primary">
                                                            {formatCurrency(c.valeur_achat)}
                                                        </td>
                                                        <td className="text-end text-success">
                                                            {formatCurrency(c.valeur_vente)}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <ProgressBar 
                                                                    now={pourcentage} 
                                                                    variant="primary"
                                                                    style={{ height: '8px', width: '120px', borderRadius: '4px' }}
                                                                />
                                                                <small className="text-muted">{pourcentage.toFixed(1)}%</small>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue détails avec top produits */}
                    {viewMode === 'details' && (
                        <>
                            {/* Top produits */}
                            <Card className="border-0 shadow-sm mb-4 table-card">
                                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="ri-trophy-line me-2 text-warning"></i>
                                        Top 10 produits par valeur
                                    </h5>
                                    <Badge bg="warning" className="px-3 py-2">
                                        Plus haute valeur
                                    </Badge>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="mb-0 align-middle custom-table">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Réf.</th>
                                                    <th>Produit</th>
                                                    <th className="text-center">Qté</th>
                                                    <th className="text-end">Prix achat</th>
                                                    <th className="text-end">Prix vente</th>
                                                    <th className="text-end">Valeur achat</th>
                                                    <th className="text-end">Valeur vente</th>
                                                    <th className="text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.top_produits?.map((p, index) => (
                                                    <tr key={index} style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                    }}>
                                                        <td>
                                                            <Badge bg="light" text="dark" className="px-3 py-2 font-mono">
                                                                {p.reference}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Link to={`/stock/produits/${p.id_produit}`} className="text-primary fw-semibold text-decoration-none">
                                                                {p.designation}
                                                            </Link>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="fw-bold">{p.quantite_reelle}</span>
                                                        </td>
                                                        <td className="text-end text-primary">{formatCurrency(p.prix_achat_ht)}</td>
                                                        <td className="text-end text-success">{formatCurrency(p.prix_vente_ht)}</td>
                                                        <td className="text-end fw-bold text-primary">{formatCurrency(p.valeur_achat)}</td>
                                                        <td className="text-end text-success">{formatCurrency(p.valeur_vente)}</td>
                                                        <td className="text-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="rounded-circle"
                                                                    style={{ width: '32px', height: '32px' }}
                                                                    onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Graphique de répartition (simulé) */}
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h5 className="mb-0">
                                        <i className="ri-pie-chart-2-line me-2 text-primary"></i>
                                        Répartition de la valeur
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-4">
                                        {data.par_categorie?.slice(0, 5).map((c, index) => {
                                            const pourcentage = (c.valeur_achat / data.global?.valeur_achat) * 100;
                                            const colors = ['#4361ee', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
                                            return (
                                                <Col md={6} key={index}>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="text-muted small">{c.nom_categorie || 'Sans catégorie'}</span>
                                                            <span className="fw-semibold">{formatCurrency(c.valeur_achat)}</span>
                                                        </div>
                                                        <ProgressBar 
                                                            now={pourcentage} 
                                                            variant="primary"
                                                            style={{ 
                                                                height: '8px', 
                                                                borderRadius: '4px',
                                                                backgroundColor: '#e9ecef'
                                                            }}
                                                        />
                                                        <small className="text-muted float-end mt-1">{pourcentage.toFixed(1)}%</small>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                    }

                    .progress {
                        background-color: #e9ecef;
                        border-radius: 10px;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}